
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash, EyeOff, Check, GripHorizontal } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useWaterDashboard } from '@/contexts/water';
import { cn } from '@/lib/utils';

interface DraggableKpiCardProps {
  id: string;
  title: string;
  children: React.ReactNode;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

const DraggableKpiCard = ({ id, title, children, className, size = 'medium' }: DraggableKpiCardProps) => {
  const { 
    isEditMode, 
    updateKpiCard, 
    removeKpiCard,
    dragStart, 
    dragOver, 
    draggedCardId,
    dragOverCardId 
  } = useWaterDashboard();
  const [editTitle, setEditTitle] = useState(title);
  const [isDragging, setIsDragging] = useState(false);
  const [isOver, setIsOver] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    dragStart(id);
    
    // Set the drag data
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(true);
    dragOver(id);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setIsOver(false);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    setIsOver(false);
  };

  // Determine size classes based on the size prop
  const sizeClasses = {
    small: "min-h-[160px] w-auto min-w-[200px]",
    medium: "min-h-[200px] w-auto min-w-[250px]",
    large: "min-h-[300px] w-auto min-w-[350px]"
  };

  return (
    <div 
      className={cn(
        "relative group flex-grow flex-shrink",
        sizeClasses[size],
        isDragging ? 'opacity-50' : '',
        isOver ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : '',
        className
      )}
      draggable={isEditMode}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      data-card-id={id}
    >
      <Card 
        className={cn(
          "h-full w-full transition-all shadow-md hover:shadow-lg rounded-lg bg-gradient-to-br from-white to-cyan-50 dark:from-gray-800 dark:to-cyan-950/30 backdrop-blur-sm border border-cyan-100 dark:border-cyan-950/50",
          isEditMode && "border-blue-300 dark:border-blue-700",
          isOver && "border-blue-500 dark:border-blue-500"
        )}
      >
        {isEditMode && (
          <div className="absolute top-2 right-2 flex space-x-1 z-10">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/80 dark:bg-gray-800/80 shadow-sm">
                  <Edit className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit KPI Card</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input 
                      id="title" 
                      value={editTitle} 
                      onChange={(e) => setEditTitle(e.target.value)} 
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <DialogClose asChild>
                    <Button onClick={() => updateKpiCard(id, { title: editTitle })}>
                      <Check className="mr-2 h-4 w-4" />
                      Save Changes
                    </Button>
                  </DialogClose>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 bg-white/80 dark:bg-gray-800/80 shadow-sm"
              onClick={() => updateKpiCard(id, { visible: false })}
            >
              <EyeOff className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-500 bg-white/80 dark:bg-gray-800/80 shadow-sm"
              onClick={() => removeKpiCard(id)}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {isEditMode && (
          <div className="absolute top-2 left-2 flex space-x-1 z-10">
            <div className="cursor-grab flex items-center justify-center h-8 w-8 bg-white/90 dark:bg-gray-800/90 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
              <GripHorizontal className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        )}
        
        <div className="font-medium text-sm text-cyan-700 dark:text-cyan-300 mt-2 mb-1 px-4">
          {title}
        </div>
        <div className="h-full w-full px-4 pb-3">
          {children}
        </div>
      </Card>
    </div>
  );
};

export default DraggableKpiCard;
